// src/components/Admin/TblGenericManager.js
//
// Admin CRUD form for the `tblgeneric` lookup table.
// Backend endpoints:
//   GET    /api/generic           (?header=&section=&q=&active=)
//   GET    /api/generic/headers
//   GET    /api/generic/sections  (?header=)
//   POST   /api/generic
//   PUT    /api/generic/{id}
//   PATCH  /api/generic/{id}/toggle
//   DELETE /api/generic/{id}
//
// Drop this anywhere in the app, e.g. as a route in App.js:
//   <Route path="/admin/generic" element={<TblGenericManager />} />

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';

const emptyForm = {
  id: null,
  headername: '',
  sectionname: '',
  value: '',
  isactive: 1,
};

const TblGenericManager = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [sections, setSections] = useState([]);
  const [filterHeader, setFilterHeader] = useState('');
  const [filterSection, setFilterSection] = useState('');
  const [filterActive, setFilterActive] = useState(''); // '', '1', '0'
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const isEdit = Boolean(form.id);

  // ── data loading ──────────────────────────────────────────────────────
  const loadHeaders = useCallback(async () => {
    try {
      const res = await api.get('/generic/headers');
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setHeaders(list);
    } catch (err) {
      console.warn('Failed to load headers:', err?.message || err);
    }
  }, []);

  const loadSections = useCallback(async (header) => {
    try {
      const res = await api.get('/generic/sections', {
        params: header ? { header } : {},
      });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setSections(list);
    } catch (err) {
      console.warn('Failed to load sections:', err?.message || err);
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterHeader)  params.header = filterHeader;
      if (filterSection) params.section = filterSection;
      if (search)        params.q = search;
      if (filterActive === '1' || filterActive === '0') params.active = filterActive;
      const res = await api.get('/generic', { params });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(list);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load entries.');
    } finally {
      setLoading(false);
    }
  }, [filterHeader, filterSection, filterActive, search]);

  useEffect(() => { loadHeaders(); }, [loadHeaders]);
  useEffect(() => { loadSections(filterHeader); }, [loadSections, filterHeader]);
  useEffect(() => { loadRows(); }, [loadRows]);

  // ── form actions ──────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setForm({
      ...emptyForm,
      headername: filterHeader || '',
      sectionname: filterSection || '',
    });
    setFormError('');
    setShowForm(true);
  }, [filterHeader, filterSection]);

  const openEdit = useCallback((row) => {
    setForm({
      id: row.id,
      headername: row.headername || '',
      sectionname: row.sectionname || '',
      value: row.value || '',
      isactive: Number(row.isactive ?? 1) ? 1 : 0,
    });
    setFormError('');
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setForm(emptyForm);
    setFormError('');
  }, []);

  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const submitForm = useCallback(async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.headername.trim()) return setFormError('Header is required.');
    if (!form.sectionname.trim()) return setFormError('Section is required.');
    if (!form.value.trim())       return setFormError('Value is required.');

    const payload = {
      headername: form.headername.trim(),
      sectionname: form.sectionname.trim(),
      value: form.value.trim(),
      isactive: Number(form.isactive) ? 1 : 0,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/generic/${form.id}`, payload);
        setSuccessMsg('Entry updated.');
      } else {
        await api.post('/generic', payload);
        setSuccessMsg('Entry created.');
      }
      closeForm();
      await loadRows();
      await loadHeaders();
      await loadSections(filterHeader);
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          (isEdit ? 'Update failed.' : 'Create failed.')
      );
    } finally {
      setSaving(false);
    }
  }, [closeForm, filterHeader, form, isEdit, loadHeaders, loadRows, loadSections]);

  const toggleActive = useCallback(async (row) => {
    try {
      await api.patch(`/generic/${row.id}/toggle`);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, isactive: Number(r.isactive) ? 0 : 1 } : r
        )
      );
      setSuccessMsg(`Entry "${row.value}" toggled.`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Toggle failed.');
    }
  }, []);

  const deleteRow = useCallback(async (row) => {
    if (!window.confirm(`Permanently delete "${row.value}"?`)) return;
    try {
      await api.delete(`/generic/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setSuccessMsg('Entry deleted.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Delete failed.');
    }
  }, []);

  // Auto-clear status messages.
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 3000);
    return () => clearTimeout(t);
  }, [successMsg]);

  const rowsByGroup = useMemo(() => rows, [rows]);

  // ── render ────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 className="mb-0">Generic Lookup Manager</h5>
          <small className="text-muted">
            Add and edit dropdown values stored in <code>tblgeneric</code>
          </small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => navigate('/logout')}
          >
            <i className="bi bi-box-arrow-right me-1"></i>
            Logout
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate('/admin/users')}
          >
            <i className="bi bi-people me-1"></i>
            Users
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate('/admin/music-on-hold')}
          >
            <i className="bi bi-music-note-beamed me-1"></i>
            Music on Hold
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={loadRows}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>
            Refresh
          </button>
          <button className="btn btn-sm btn-primary" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1"></i>
            New Entry
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2 small d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close btn-close-sm" onClick={() => setError('')}></button>
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success py-2 small d-flex justify-content-between">
          <span>{successMsg}</span>
          <button className="btn-close btn-close-sm" onClick={() => setSuccessMsg('')}></button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label small mb-1">Header</label>
              <select
                className="form-select form-select-sm"
                value={filterHeader}
                onChange={(e) => {
                  setFilterHeader(e.target.value);
                  setFilterSection('');
                }}
              >
                <option value="">All headers</option>
                {headers.map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label small mb-1">Section</label>
              <select
                className="form-select form-select-sm"
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
                disabled={!filterHeader && sections.length === 0}
              >
                <option value="">All sections</option>
                {sections.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label small mb-1">Search value</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="e.g. CNIC"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
              >
                <option value="">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div className="col-12 col-md-1">
              <button
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={() => {
                  setFilterHeader('');
                  setFilterSection('');
                  setSearch('');
                  setFilterActive('');
                }}
                title="Clear filters"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center text-muted py-4 small">Loading…</div>
          ) : rowsByGroup.length === 0 ? (
            <div className="text-center text-muted py-4 small">
              No entries match the current filters.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0 small align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '60px' }}>ID</th>
                    <th>Header</th>
                    <th>Section</th>
                    <th>Value</th>
                    <th style={{ width: '110px' }}>Status</th>
                    <th className="text-end" style={{ width: '180px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsByGroup.map((row) => {
                    const active = Number(row.isactive ?? 0) === 1;
                    return (
                      <tr key={row.id}>
                        <td className="text-muted">{row.id}</td>
                        <td>{row.headername}</td>
                        <td>{row.sectionname}</td>
                        <td className="fw-semibold">{row.value}</td>
                        <td>
                          <span className={`badge ${active ? 'bg-success' : 'bg-secondary'}`}>
                            {active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEdit(row)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className={`btn btn-sm me-1 ${active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            onClick={() => toggleActive(row)}
                            title={active ? 'Deactivate' : 'Activate'}
                          >
                            <i className={`bi ${active ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteRow(row)}
                            title="Delete permanently"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Form modal */}
      {showForm && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeForm();
          }}
        >
          <div className="modal-dialog" role="document">
            <form className="modal-content" onSubmit={submitForm}>
              <div className="modal-header py-2">
                <h6 className="modal-title">
                  {isEdit ? 'Edit Entry' : 'New Entry'}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeForm}
                  disabled={saving}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Header <span className="text-danger">*</span>
                  </label>
                  <input
                    list="generic-headers-list"
                    type="text"
                    className="form-control form-control-sm"
                    value={form.headername}
                    onChange={(e) => updateField('headername', e.target.value)}
                    placeholder="e.g. Complaint"
                    disabled={saving}
                    autoFocus={!isEdit}
                  />
                  <datalist id="generic-headers-list">
                    {headers.map((h) => (
                      <option key={h} value={h} />
                    ))}
                  </datalist>
                </div>

                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Section <span className="text-danger">*</span>
                  </label>
                  <input
                    list="generic-sections-list"
                    type="text"
                    className="form-control form-control-sm"
                    value={form.sectionname}
                    onChange={(e) => updateField('sectionname', e.target.value)}
                    placeholder="e.g. WhatsappDocumentType"
                    disabled={saving}
                  />
                  <datalist id="generic-sections-list">
                    {sections.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>

                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Value <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={form.value}
                    onChange={(e) => updateField('value', e.target.value)}
                    placeholder="Display text shown in the dropdown"
                    maxLength={500}
                    disabled={saving}
                  />
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="generic-active"
                    checked={Number(form.isactive) === 1}
                    onChange={(e) => updateField('isactive', e.target.checked ? 1 : 0)}
                    disabled={saving}
                  />
                  <label className="form-check-label small" htmlFor="generic-active">
                    Active (shown in dropdowns)
                  </label>
                </div>

                {formError && (
                  <div className="alert alert-danger py-2 small mb-0 mt-2">{formError}</div>
                )}
              </div>
              <div className="modal-footer py-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving…
                    </>
                  ) : isEdit ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TblGenericManager;
